/**
 * Member roles as defined by the backend `MemberRole` value object:
 * `Owner` (4), `Admin` (3), `Collaborator` (2), `Client` (1).
 *
 * @remarks
 * Modeled as a constant-object enum so the underlying value can later switch
 * from the lowercase string identifier to the numeric permission level
 * (`ROLE_LEVEL`) in a single place — consumers reference `MemberRole.Owner`,
 * never the raw literal.
 *
 * The app uses the lowercase identifier internally; the backend uses the
 * PascalCase name (`ROLE_API_NAME`). Convert at the API boundary with
 * `roleFromApi` (inbound, case-insensitive) and `roleToApi` (outbound).
 */
export const MemberRole = {
  Owner: 'owner',
  Admin: 'admin',
  Collaborator: 'collaborator',
  Client: 'client',
} as const;

export type MemberRole = (typeof MemberRole)[keyof typeof MemberRole];

/**
 * All roles in descending privilege order — used as the source of truth for
 * selects, iteration and validation. No API endpoint returns this list.
 */
export const MEMBER_ROLES = [
  MemberRole.Owner,
  MemberRole.Admin,
  MemberRole.Collaborator,
  MemberRole.Client,
] as const;

/**
 * Roles assignable to a project member via invite/edit-role UI — excludes
 * `Owner`, which is fixed to the project creator and not reassignable here.
 */
export const ASSIGNABLE_PROJECT_ROLES = MEMBER_ROLES.filter(
  (role): role is Exclude<MemberRole, typeof MemberRole.Owner> => role !== MemberRole.Owner,
);

/** Backend permission hierarchy (higher number = more privileges). */
export const ROLE_LEVEL: Record<MemberRole, number> = {
  [MemberRole.Owner]: 4,
  [MemberRole.Admin]: 3,
  [MemberRole.Collaborator]: 2,
  [MemberRole.Client]: 1,
};

/** PascalCase identifier expected/returned by the backend for each role. */
export const ROLE_API_NAME: Record<MemberRole, string> = {
  [MemberRole.Owner]: 'Owner',
  [MemberRole.Admin]: 'Admin',
  [MemberRole.Collaborator]: 'Collaborator',
  [MemberRole.Client]: 'Client',
};

const ROLE_BY_LOWERCASE: Record<string, MemberRole> = {
  owner: MemberRole.Owner,
  admin: MemberRole.Admin,
  collaborator: MemberRole.Collaborator,
  client: MemberRole.Client,
};

/**
 * Normalizes a raw backend role string (any casing) to a `MemberRole`.
 * Falls back to `Collaborator` for unknown or null values — the backend does
 * not enforce the enum on every payload.
 */
export function roleFromApi(raw: string | null | undefined): MemberRole {
  return ROLE_BY_LOWERCASE[raw?.toLowerCase() ?? ''] ?? MemberRole.Collaborator;
}

/** Converts an internal `MemberRole` to the PascalCase identifier the backend expects. */
export function roleToApi(role: MemberRole): string {
  return ROLE_API_NAME[role];
}
